import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useAccount } from 'wagmi';
import { Typography, Table, Tag, Button, Space, Modal, Form, Input, Radio, Checkbox, message } from 'antd';
import DashboardLayout from '../components/DashboardLayout';
import { useWriteContract, useTransactionConfirmations, useReadContract } from 'wagmi';
import { CONTRACT_ADDRESS, CONTRACT_ABI, getContractInstance } from '../config/contractConfig';
import { BASE_URL } from "../utils/URL_Config"; 


const { Title } = Typography;

type ProjectData = {
  key: string;
  projectId: string;
  url: string;
  amount: number;
  mode: 'Default' | 'Custom';
  customTokenAddress?: string;
  defaultTokenAddress?: string; 
  autoReward: boolean;
  status: string;
  createdAt: string;
};

const Projects: React.FC = () => {
  const [dataSource, setDataSource] = useState<ProjectData[]>([]); 
  const [isModalVisible, setIsModalVisible] = useState(false); 
  const [form] = Form.useForm(); // Form instance
  const [mode, setMode] = useState<'Default' | 'Custom'>('Default'); 
  const [isCreating, setIsCreating] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null); 

  const { writeContractAsync, isSuccess, isError, isPending } = useWriteContract();

  const { address: userAddress } = useAccount(); // Get the connected wallet address


  // Fetch projects created by the current user
  const { data: userProjects, isLoading } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'getAddressProjects',
    args: [userAddress, 0, 15], // Fetch the first 10 projects (pagination)
  });


  //fetching default token address
  const { data: defaultTokenAddress } = useReadContract({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    functionName: 'defaultRewardToken',
  });
  

  useEffect(() => {
    if (userProjects && Array.isArray(userProjects) && defaultTokenAddress) {
      const formattedProjects = userProjects.map((project: any, index: number) => {
        const isDefaultToken = project.tokenContract === defaultTokenAddress;
  
        return {
          key: index.toString(),
          projectId: project.projectId,
          url: project.url,
          amount: parseFloat(ethers.formatUnits(project.reward, 'ether')), // Convert reward to number
          mode: isDefaultToken ? ('Default' as 'Default' | 'Custom') : ('Custom' as 'Default' | 'Custom'), // Explicitly cast mode
          customTokenAddress: isDefaultToken ? 'N/A' : project.tokenContract, // Set custom token address only for custom tokens
          defaultTokenAddress: isDefaultToken ? (defaultTokenAddress as string) : 'N/A', // Ensure defaultTokenAddress is string
          autoReward: project.autoReward, // Preserve autoReward flag
          status: 'Active',
          createdAt: new Date().toISOString().split('T')[0], // Set current date
        };
      });
      setDataSource(formattedProjects);
    }
  }, [userProjects, defaultTokenAddress]);
  
  
  
  
  

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields(); 
  };

  const handleCreateProject = async (values: any) => {
    setIsCreating(true);

    try {
      const { url, amount, autoReward, customTokenAddress } = values;
      const args =
        mode === 'Default'
          ? [url, ethers.parseUnits(amount.toString(), 'ether'), autoReward]
          : [url, ethers.parseUnits(amount.toString(), 'ether'), autoReward, customTokenAddress];

      const functionName = mode === 'Default' ? 'createProjectDefaultToken' : 'createProjectCustomToken';
      const value = ethers.parseUnits(mode === 'Default' ? '1' : '10', 'ether');

      const tx = await writeContractAsync({
        abi: CONTRACT_ABI,
        address: CONTRACT_ADDRESS,
        functionName,
        args,
        value,
      });

      setTxHash(tx);

      message.success('Transaction sent to the blockchain!');

      if (isPending) {
        console.log('Transaction pending:', isPending);
      }

      if (isError) {
        console.log('Transaction failed:', isError);
      }

      if (isSuccess) {
        console.log('Transaction confirmed:', isSuccess);
        const newProject: ProjectData = {
          key: `${dataSource.length + 1}`,
          projectId: '',
          url: form.getFieldValue('url'),
          amount: Number(form.getFieldValue('amount')),
          mode,
          customTokenAddress: form.getFieldValue('customTokenAddress') || '',
          autoReward: form.getFieldValue('autoReward') || false,
          status: 'Active',
          createdAt: new Date().toISOString().split('T')[0],
          defaultTokenAddress: mode === 'Default' ? CONTRACT_ADDRESS : '', // Show Default token address if mode is Default
        };

        setDataSource([...dataSource, newProject]);
        setIsModalVisible(false);
        form.resetFields();
      }
    } catch (error: any) {
      console.error('Error creating project:', error.message);
      message.error('Failed to create project on the blockchain.');
    } finally {
      setIsCreating(false);
    }
  };


  const generatePromotionLink = (projectId: string) => {
    // Generate the promotion link containing the projectId
    const promotionLink = `${BASE_URL}/invite-code?projectid=${projectId}`;
    // Copy the promotion link to the clipboard
    navigator.clipboard.writeText(promotionLink)
      .then(() => {
        message.success('Promotion link copied to clipboard!');
      })
      .catch(() => {
        message.error('Failed to copy the promotion link. Please try again.');
      });
  };

  const handleClaimReward = async (projectId: string) => {
    try {
      const inviteeAddress = prompt('Enter the invitee address to claim reward:'); // Prompt for the invitee address
      if (!inviteeAddress) {
        message.error('Invitee address is required.');
        return;
      }
  
      const tx = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'claimReward',
        args: [projectId, inviteeAddress], // Pass projectId and invitee address
      });
  
      if (isPending) {
        console.log('Claim Reward pending:', isPending);
      }

      if (isError) {
        console.log('Claim Reward failed:', isError);
      }

      if (isSuccess) {
        console.log('Claim Reward Successful:', isSuccess);
      }

      message.success('Reward successfully claimed!');
    } catch (error: any) {
      console.error('Error claiming reward:', error);
      message.error('Failed to claim reward. Please try again.');
    }
  };
  

  const generateInviteCode = async (projectId: string) => {
    try {
      const tx = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'createInviteCode',
        args: [projectId],
      });

      //await tx.wait(); // Wait for the transaction to be mined

      if (isPending) {
        console.log('Generating invite code pending:', isPending);
      }

      if (isError) {
        console.log('Generating invite code failed:', isError);
      }

      if (isSuccess) {
        console.log('Invite code successfully generated:', isSuccess);
      }

      message.success(`Invite code successfully generated for project ID: ${projectId}`);
    } catch (error: any) {
      console.error('Error generating invite code:', error);
      message.error('Failed to generate invite code. Please try again.');
    }
  };

  const columns = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Token Mode',
      dataIndex: 'mode',
      key: 'mode',
    },
    {
      title: 'Custom Token Address',
      dataIndex: 'customTokenAddress',
      key: 'customTokenAddress',
      render: (address: string, record: ProjectData) => (record.mode === 'Custom' ? address : 'N/A'),
    },
    {
      title: 'Default Token Address',
      dataIndex: 'defaultTokenAddress', 
      key: 'defaultTokenAddress',
      render:(address: string, record: ProjectData) => (record.mode === 'Default' ? address : 'N/A'),
    },
    {
      title: 'Auto Reward',
      dataIndex: 'autoReward',
      key: 'autoReward',
      render: (autoReward: boolean) =>
        autoReward ? <Tag color="green">Enabled</Tag> : <Tag color="red">Disabled</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Active' ? 'green' : 'volcano'}>{status}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Actions',
      key: 'actions', 
      render: (text: string, record: ProjectData) => (
        <Space>
          <Button type="link" onClick={() => generatePromotionLink(record.projectId)}>
            Generate Promotion Link
          </Button>
          {/* Claim Reward Button for non-auto-reward projects */}
        {!record.autoReward && (
          <Button
            type="link"
            onClick={() => handleClaimReward(record.projectId)}
          >
            Claim Reward
          </Button>
        )}
          <Button type="link">Edit</Button>
          <Button type="link" danger>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: '1rem' }}>
        <Title level={3}>Projects</Title>
        <p>Manage your projects from this page. You can view, edit, and delete projects.</p>
        <Button type="primary" onClick={showModal} style={{ marginBottom: '1rem' }}>
          Create Project
        </Button>
        <Table
          dataSource={dataSource}
          columns={columns}
          bordered
          style={{ marginTop: '1rem' }}
          pagination={{ pageSize: 10 }}
        />
        <Modal
          title="Create New Project"
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={null}
        >
          <Form form={form} onFinish={handleCreateProject} layout="vertical" initialValues={{autoReward: true}}>
            <Form.Item
              name="url"
              label="Project URL"
              rules={[{ required: true, message: 'Please enter the project URL' }]}
            >
              <Input placeholder="Enter project URL" />
            </Form.Item>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: 'Please enter the project amount' }]}
            >
              <Input placeholder="Enter project amount" type="number" />
            </Form.Item>
            <Form.Item name="autoReward" valuePropName="checked">
              <Checkbox>Enable Auto Reward</Checkbox>
            </Form.Item>
            <Form.Item
              name="mode"
              label="Token Mode"
              rules={[{ required: true, message: 'Please select a token mode' }]}
            >
              <Radio.Group onChange={(e) => setMode(e.target.value)} value={mode}>
                <Radio value="Default">Default Token</Radio>
                <Radio value="Custom">Custom Token</Radio>
              </Radio.Group>
            </Form.Item>
            {mode === 'Custom' && (
              <Form.Item
                name="customTokenAddress"
                label="Custom Token Address"
                rules={[{ required: true, message: 'Please enter the custom token address' }]}
              >
                <Input placeholder="Enter custom token address" />
              </Form.Item>
            )}
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isCreating}>
                  Create
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default Projects;